import { ValidatorFn } from '@angular/forms';

export type FieldInputType = 'text' | 'dropdown';
export type OptionSource = 'commsOptions' | 'provinceList';

export type VoucherFieldType = 'text' | 'number' | 'dropdown' | 'checkbox';

export type VoucherFieldFormat = 'decimal';

export interface VoucherFieldConfig {
	controlName: string;
	label: string;
	type: VoucherFieldType;
	placeholder?: string;
	optionsSource?: 'stores';
	displayKey?: string;
	valueKey?: string;
	format?: VoucherFieldFormat;
	displayWhen?: {
		controlName: string;
		equals: boolean | string | number;
	};
}

export interface VoucherFormConfig {
	visible: boolean;
	fields: VoucherFieldConfig[];
}

export interface FieldConfig {
	controlName: string;
	label: string;
	type?: FieldInputType;
	placeholder?: string;
	optionsSource?: OptionSource;
	options?: unknown[];
	displayKey?: string;
	valueKey?: string;
	inputProps?: {
		noEndingSpace?: boolean;
	};
	// Optional validators that can be applied when creating controls dynamically
	validators?: ValidatorFn[];
	// Optional store path(s) for mapping form values to store structure
	// Can be a single string path or array of fallback paths
	storePath?: string | string[];
}

export interface SectionConfig {
	visible: boolean;
	fields: FieldConfig[];
}

export interface AddressSectionConfig {
	visible: boolean;
	commonFields: FieldConfig[];
	houseEstateFields: FieldConfig[];
	flatOfficeFields: FieldConfig[];
}

export interface AdditionalIdentity {
	identity_type: string;
	description: string;
}

export interface CustomerDetailConfig {
	customerDetails: SectionConfig;
	contactDetails: SectionConfig;
	addressDetails: AddressSectionConfig;
	voucherForm?: VoucherFormConfig;
	additional_identities?: AdditionalIdentity[];
}

/**
 * Converts additional_identities from tenant config into VoucherFieldConfig entries.
 * Each identity becomes a required text input field with controlName `identity_<identity_type>`.
 */
export function buildAdditionalIdentityFields(identities?: AdditionalIdentity[]): VoucherFieldConfig[] {
	return (identities ?? []).map(identity => ({
		controlName: `identity_${identity.identity_type}`,
		label: identity.description,
		type: 'text' as VoucherFieldType
	}));
}

export const DEFAULT_TENANT_DETAIL_CONFIG: CustomerDetailConfig = {
	customerDetails: {
		visible: true,
		fields: [
			{ controlName: 'first_name', label: 'first_name', storePath: 'applicant.first_name' },
			{ controlName: 'surname', label: 'surname', storePath: 'applicant.surname' },
			// { controlName: 'id_number', label: 'id_number', storePath: ['applicant.id_number', 'applicant.information.id_number'] },
			// { controlName: 'customer_reference', label: 'customer_reference', storePath: 'applicant.customerreference' },
			// { controlName: 'sales_order_number', label: 'sales_order_number', storePath: ['applicant.salesordernumber', 'loan_information.salesordernumber'] }
		]
	},
	contactDetails: {
		visible: true,
		fields: [
			{ controlName: 'contact_number', label: 'contact_number', storePath: 'applicant.contact_number' },
			{
				controlName: 'cell_number',
				label: 'cell_number',
				storePath: ['applicant.cell_number', 'applicant.cellnumber', 'loan_information.cellnumber']
			},
			{ controlName: 'email', label: 'email', storePath: ['loan_information.email', 'applicant.email'] },
			{
				controlName: 'preferred_com_method',
				label: 'preferred_com_method',
				type: 'dropdown',
				optionsSource: 'commsOptions',
				placeholder: 'Select method',
				storePath: ['loan_information.preferedcommethod', 'loan_information.preferred_com_method']
			}
		]
	},
	addressDetails: {
		visible: true,
		commonFields: [
			{
				controlName: 'province',
				label: 'province',
				type: 'dropdown',
				optionsSource: 'provinceList',
				placeholder: 'Select Province',
				storePath: 'loan_information.address.claimprovince'
			}
		],
		houseEstateFields: [
			{ controlName: 'complex', label: 'estate_name', storePath: ['loan_information.address.complex', 'loan_information.propertycomplex'] },
			{ controlName: 'street_address', label: 'street_address', storePath: 'loan_information.address.address' },
			{ controlName: 'suburb', label: 'suburb', storePath: 'loan_information.address.suburb' },
			{ controlName: 'city', label: 'city', inputProps: { noEndingSpace: true }, storePath: 'loan_information.address.city' },
			{ controlName: 'postal_code', label: 'postal_code', inputProps: { noEndingSpace: true }, storePath: 'loan_information.address.postal_code' }
		],
		flatOfficeFields: [
			{ controlName: 'complex', label: 'Flat/Office Name', storePath: ['loan_information.address.complex', 'loan_information.propertycomplex'] },
			{ controlName: 'complex_unit_number', label: 'unit_number', storePath: ['loan_information.address.complex_unit_number', 'loan_information.propertycomplexunitnumber'] },
			{ controlName: 'complex_block', label: 'block' },
			{ controlName: 'street_address', label: 'street_address', storePath: 'loan_information.address.address' },
			{ controlName: 'suburb', label: 'suburb', storePath: 'loan_information.address.suburb' },
			{ controlName: 'city', label: 'city', inputProps: { noEndingSpace: true }, storePath: 'loan_information.address.city' },
			{ controlName: 'postal_code', label: 'postal_code', inputProps: { noEndingSpace: true }, storePath: 'loan_information.address.postal_code' }
		]
	},
	voucherForm: {
		visible: true,
		fields: [
			{ controlName: 'quantity', label: 'Quantity', type: 'number' },
			{
				controlName: 'store_poi',
				label: 'Store purchased from',
				type: 'dropdown',
				optionsSource: 'stores',
				displayKey: 'display',
				valueKey: 'value',
				placeholder: 'Select store'
			},
			{ controlName: 'what_is_being_installed', label: 'What is being installed', type: 'text' },
			{ controlName: 'collection_required', label: 'Collection required', type: 'checkbox' },
			{
				controlName: 'store_has_stock',
				label: 'Store has stock',
				type: 'checkbox',
				displayWhen: { controlName: 'collection_required', equals: true }
			},
			{
				controlName: 'delivery_cost',
				label: 'Additional delivery voucher cost (only if required)',
				type: 'text',
				format: 'decimal',
				displayWhen: { controlName: 'store_has_stock', equals: true }
			},
			{
				controlName: 'where_to_collect',
				label: 'Where to collect from',
				type: 'text',
				displayWhen: { controlName: 'collection_required', equals: true }
			}
		]
	}
};

/**
 * Gets the tenant-specific customer detail config with fallback logic:
 * 1. Check metadata.customer_detail_config (highest priority - from backend/company selection)
 * 2. Check tenantOverrides[tenantId] (tenant-specific frontend overrides)
 * 3. Fall back to DEFAULT_TENANT_DETAIL_CONFIG
 */
export function getTenantConfig(
	tenantId: number | null | undefined,
	metadata?: { customer_detail_config?: CustomerDetailConfig } | null,
	tenantOverrides?: Record<string, CustomerDetailConfig>
): CustomerDetailConfig {
	// 1. Check metadata first (highest priority)
	if (metadata?.customer_detail_config) {
		return metadata.customer_detail_config;
	}

	// 2. Check tenant-specific override
	if (tenantId != null && tenantOverrides?.[String(tenantId)]) {
		return tenantOverrides[String(tenantId)];
	}

	// 3. Fall back to default
	return DEFAULT_TENANT_DETAIL_CONFIG;
}

/**
 * Gets the voucher form config with fallback logic:
 * 1. Check metadata.customer_detail_config.voucherForm (highest priority)
 * 2. Check tenantOverrides[tenantId].voucherForm (tenant-specific overrides)
 * 3. Fall back to DEFAULT_TENANT_DETAIL_CONFIG.voucherForm
 */
export function getVoucherFormConfig(
	tenantId: number | null | undefined,
	metadata?: { customer_detail_config?: CustomerDetailConfig } | null,
	tenantOverrides?: Record<string, CustomerDetailConfig>
): VoucherFormConfig | undefined {
	// 1. Check metadata first (highest priority)
	if (metadata?.customer_detail_config?.voucherForm) {
		return metadata.customer_detail_config.voucherForm;
	}

	// 2. Check tenant-specific override
	if (tenantId != null && tenantOverrides?.[String(tenantId)]?.voucherForm) {
		return tenantOverrides[String(tenantId)].voucherForm;
	}

	// 3. Fall back to default
	return DEFAULT_TENANT_DETAIL_CONFIG.voucherForm;
}

